import { createStore, applyMiddleware, compose } from "redux";
import { createLogger } from "redux-logger";

import thunkMiddleware from "redux-thunk";
import MainReducer from "./reducers/main_reducers";
import {
  createReduxEnhancer,
  init as initSentry,
  rewriteFramesIntegration,
} from "@sentry/react";

const loggerMiddleware = createLogger();

import CONST from "../webApp/CONST";
import { IsRunningAsBrowser } from "../util/rendererEnvironment";

const isPrerenderBuild = process.env.LR_PRERENDER === "true";
const kUnattributedIosSafariRuntimeMessage =
  "Invalid call to runtime.sendMessage(). Tab not found.";
const kIosSafariBrowserName = "Mobile Safari";
const kIosOperatingSystemName = "iOS";
const kMacSafariBrowserName = "Safari";
const kMacOperatingSystemName = "Mac OS X";
const kWebkitMaskedFilename = "webkit-masked-url://hidden/";
const kUnattributedMacSafariErrorMessages = [
  "undefined is not an object (evaluating 't.tab.customFillData')",
  "undefined is not an object (evaluating 'L.A.getState().system.isHighContrast')",
];
const kUnhandledRejectionMechanism = "onunhandledrejection";

const FilterKnownUnattributedSafariRejection = (event) => {
  const isIosSafari =
    event.contexts?.browser?.name === kIosSafariBrowserName &&
    event.contexts?.os?.name === kIosOperatingSystemName;
  const isUnattributedRuntimeRejection = event.exception?.values?.some(
    (exception) =>
      exception.value === kUnattributedIosSafariRuntimeMessage &&
      exception.mechanism?.type === kUnhandledRejectionMechanism &&
      !exception.stacktrace?.frames?.length,
  );
  const isMacSafari =
    event.contexts?.browser?.name === kMacSafariBrowserName &&
    event.contexts?.os?.name === kMacOperatingSystemName;
  const isKnownUnattributedMacSafariRejection =
    event.exception?.values?.some((exception) => {
      const frames = exception.stacktrace?.frames;
      return (
        kUnattributedMacSafariErrorMessages.includes(exception.value) &&
        exception.mechanism?.type === kUnhandledRejectionMechanism &&
        frames?.length > 0 &&
        frames.every((frame) => frame.filename === kWebkitMaskedFilename)
      );
    }) ?? false;

  const shouldDropEvent =
    (isIosSafari && isUnattributedRuntimeRejection) ||
    (isMacSafari && isKnownUnattributedMacSafariRejection);

  return shouldDropEvent ? null : event;
};

if (
  process.env.NODE_ENV === "production" &&
  !process.env.JEST_WORKER_ID &&
  !isPrerenderBuild
) {
  //console.log  = () => { }
  console.warn = () => {
    /* Suppress console warnings in the production logging configuration. */
  };

  const rewriteFrames = rewriteFramesIntegration({
    iteratee: (frame) => {
      if (frame.filename && !IsRunningAsBrowser()) {
        const filename = frame.filename.split("static").pop();
        frame.filename = "~/static" + filename;
      }
      return frame;
    },
  });
  initSentry({
    dsn: "https://7ee2a3c714d043d99976daf7ef4d6869@sentry.io/2529975",
    release: CONST.VERSION + "+" + CONST.BUILD,
    integrations: [rewriteFrames],
    tracesSampleRate: 1.0,
    beforeSend: FilterKnownUnattributedSafariRejection,
  });
}

if (typeof window !== "undefined") {
  window.__LR__SENTRY = { init: initSentry };
}

const sentryReduxEnhancer = createReduxEnhancer({
  // Optionally pass options
});

export const store = createStore(
  MainReducer,
  compose(
    applyMiddleware(
      thunkMiddleware, // lets us dispatch() functions
      loggerMiddleware,
    ), // neat middleware that logs actions
    sentryReduxEnhancer,
  ),
);
